import first from "lodash/first";
import { type FunctionComponent, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { data as appData } from "shared/data";
import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles/theme";
import { notEmpty } from "shared/lib/utils";

import { useSectionContext } from "scmp-app/components/section/contexts";
import { getVideoYoutubeId } from "scmp-app/components/video/helpers";
import type { sectionStyleVideoWidgetQuery$key } from "scmp-app/queries/__generated__/sectionStyleVideoWidgetQuery.graphql";
import { findFirstNonEmptyString } from "scmp-app/scmp-sdk";

import { Container, StyledSCMPYoutubeVideo } from "./styles";

export type Props = {
  reference: sectionStyleVideoWidgetQuery$key;
};

export const SectionStyleVideoWidget: FunctionComponent<Props> = ({ reference }) => {
  const { advertisement: baseAdvertisement } = useSectionContext();
  const data = useFragment(
    graphql`
      fragment sectionStyleVideoWidgetQuery on Query {
        styleVideoSectionPicksQueue: queue(filter: { name: "video_section_picks_323289" }) {
          items(first: 1) {
            edges {
              node {
                __typename
                ... on Video {
                  duration
                  entityId
                  headline
                  socialHeadline
                  image(filter: { type: MAIN_IMAGE }) {
                    style(filter: { style: "wide_landscape" }) {
                      url
                    }
                  }
                  ...helpersGetVideoYoutubeIdVideo
                }
              }
            }
          }
        }
      }
    `,
    reference,
  );

  const isDesktopUp = useResponsive(theme.breakpoints.up("desktop"), false);
  const content = first(data.styleVideoSectionPicksQueue?.items?.edges)?.node;
  const isVideo = content?.__typename === "Video";
  const youtubeId = useMemo(
    () => (isVideo ? getVideoYoutubeId(content) : undefined),
    [content, isVideo],
  );

  const adInfo: ADInfo = {
    targeting: {
      ...baseAdvertisement.targeting,
      adslot: "Instream",
    },
    zone: baseAdvertisement.zone + "/instream1",
  };

  if (!isVideo || !notEmpty(youtubeId)) return null;
  return (
    <Container>
      <StyledSCMPYoutubeVideo
        adInfo={adInfo}
        duration={content.duration ?? undefined}
        enableAutoPlay={isDesktopUp}
        entityId={content.entityId}
        gaData={{ ga4Position: "footer_widget" }}
        imageSource={content.image?.style?.url ?? undefined}
        index={Number.parseInt(content.entityId)}
        isEnabledImaManager={true}
        relatedChannels={[appData.youtube.styleChannelId]}
        title={findFirstNonEmptyString(content?.socialHeadline, content?.headline)}
        withFooter={{ variant: "magazines-style", withIcon: false }}
        youtubeVideoId={youtubeId}
      />
    </Container>
  );
};

SectionStyleVideoWidget.displayName = "SectionStyleVideoWidget";
